import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@platform/ui/components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import api from "~/shared/api/core/api";
import { JOBS_KEYS, JOBS_QUERIES } from "~/shared/api/jobs/jobs.queries";
import { PARTICIPANTS_KEYS, PARTICIPANTS_QUERIES } from "~/shared/api/participants/participants.queries";
import { PARTNERS_QUERIES } from "~/shared/api/partners/partners.queries";
import { AsyncComboboxField } from "~/shared/components/ComboboxField/AsyncComboboxField";
import { ComboboxJobItem } from "~/shared/components/ComboboxJobItem";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/shared/components/ds/Form";
import { toast } from "~/shared/components/ds/Toast/Toast";

export function AddToBrainstormForm({
  participant,
  onSuccessfulSubmit,
  onCancel,
}: {
  participant: {
    id: number;
    name: string;
  };
  onSuccessfulSubmit?: () => void;
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();
  const form = useForm<AddToBrainstormFormInput>({
    mode: "onSubmit",
    resolver: zodResolver(AddToBrainstormFormSchema),
    shouldUnregister: true,
    defaultValues: {
      participant,
    },
  });
  const { handleSubmit, control, resetField } = form;
  const partner = useWatch({
    control,
    name: "partner",
  });
  const jobsQueryOptions = React.useMemo(() => getJobsListQueryOptions(partner?.id), [partner?.id]);
  const addToBrainstormMutation = useMutation({
    mutationFn: async ({ jobId, participantId }: { jobId: number; participantId: number }) => {
      await api.post(`/jobs/${jobId}/brainstorm`, {
        participants: [participantId],
      });
      return {
        jobId,
        participantId,
      };
    },
    onSuccess: ({ jobId, participantId }) => {
      queryClient.invalidateQueries({
        queryKey: [JOBS_KEYS.detail(jobId)],
      });
      queryClient.invalidateQueries({
        queryKey: PARTICIPANTS_KEYS.detail(participantId.toString()),
      });
      onSuccessfulSubmit?.();
    },
    onError: (error) => {
      toast.api.error({ error, title: "Failed to add to brainstorm" });
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit((data) => {
          addToBrainstormMutation.mutate({
            jobId: data.job.id,
            participantId: data.participant.id,
          });
        })}
        className="stack-y-6"
      >
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-6">
          <FormField
            control={control}
            name="partner"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Partner</FormLabel>
                  <FormControl>
                    <AsyncComboboxField
                      {...field}
                      displayName="Partner"
                      infiniteQueryOptions={PARTNERS_QUERIES.infinitePartnersList}
                      onComboSelect={() => {
                        resetField("job");
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={control}
            name="job"
            disabled={!partner?.id}
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Job</FormLabel>
                  <FormControl>
                    <AsyncComboboxField
                      {...field}
                      displayName="Job"
                      infiniteQueryOptions={jobsQueryOptions}
                      renderItem={(job) => <ComboboxJobItem job={job} />}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={control}
            name="participant"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Participant</FormLabel>
                  <FormControl>
                    <AsyncComboboxField
                      {...field}
                      displayName="Participant"
                      infiniteQueryOptions={PARTICIPANTS_QUERIES.infiniteParticipantsList}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
        <div>
          <div className="stack-x-4 justify-end">
            <Button
              variant="secondary"
              onClick={(e) => {
                e.preventDefault();
                onCancel?.();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={addToBrainstormMutation.isPending}
              isLoading={addToBrainstormMutation.isPending}
              type="submit"
            >
              Add to Game Plan
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

const AddToBrainstormFormSchema = z.object({
  partner: z.object(
    {
      id: z.coerce.number(),
      name: z.string(),
    },
    {
      errorMap: () => ({ message: "Please select a partner." }),
    },
  ),
  job: z.object(
    {
      id: z.coerce.number(),
      name: z.string(),
    },
    {
      errorMap: () => ({ message: "Please select a job." }),
    },
  ),
  participant: z.object(
    {
      id: z.coerce.number(),
      name: z.string(),
    },
    {
      errorMap: () => ({ message: "Please select a participant." }),
    },
  ),
});
type AddToBrainstormFormInput = z.input<typeof AddToBrainstormFormSchema>;

function getJobsListQueryOptions(partnerId?: number) {
  return (params: { [key: string]: unknown }) => {
    return JOBS_QUERIES.infiniteJobsList({
      ...params,
      ...(partnerId ? { partner: partnerId } : {}),
    });
  };
}
